import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Paper,
  TextField,
  InputAdornment,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  Chip,
  CircularProgress,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
} from '@mui/material';
import { 
  Search, 
  Chat as ChatIcon, 
  Person, 
  Computer, 
  WhatsApp 
} from '@mui/icons-material';
import { conversationsAPI, whatsappAPI } from '../../services/api';
import { selectIsSuperAdmin } from '../../store/authSlice';

const Conversations = () => {
  const CONVERSATIONS_PAGE_SIZE = 200;
  const USER_CHATS_PAGE_SIZE = 50;

  const isSuperAdmin = useSelector(selectIsSuperAdmin);
  const [conversations, setConversations] = useState([]);
  const [filteredConversations, setFilteredConversations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userChats, setUserChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMoreConversations, setLoadingMoreConversations] = useState(false);
  const [conversationsPage, setConversationsPage] = useState(1);
  const [conversationsHasMore, setConversationsHasMore] = useState(false);
  const [conversationsTotal, setConversationsTotal] = useState(0);

  const [loadingUserChats, setLoadingUserChats] = useState(false);
  const [loadingMoreUserChats, setLoadingMoreUserChats] = useState(false);
  const [userChatsPage, setUserChatsPage] = useState(1);
  const [userChatsHasMore, setUserChatsHasMore] = useState(false);
  const [userChatsTotal, setUserChatsTotal] = useState(0);
  
  // Filters
  const [sourceFilter, setSourceFilter] = useState('all'); // 'all', 'web', 'whatsapp'
  const [whatsappNumbers, setWhatsappNumbers] = useState([]);
  const [selectedWhatsAppNumber, setSelectedWhatsAppNumber] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');

  const getDateGroupLabel = (dateValue) => {
    const messageDate = new Date(dateValue);
    const today = new Date();

    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const startOfMessageDate = new Date(
      messageDate.getFullYear(),
      messageDate.getMonth(),
      messageDate.getDate()
    );

    const diffInDays = Math.floor((startOfToday - startOfMessageDate) / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';

    return messageDate.toLocaleDateString(undefined, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  useEffect(() => {
    fetchWhatsAppNumbers();
    fetchConversations(1, false);
  }, []);

  useEffect(() => {
    fetchConversations(1, false);
  }, [sourceFilter, selectedWhatsAppNumber, selectedDepartment]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = conversations.filter(conv =>
        conv.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        conv.customer_id?.includes(searchTerm) ||
        conv.message?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredConversations(filtered);
    } else {
      setFilteredConversations(conversations);
    }
  }, [searchTerm, conversations]);

  const fetchWhatsAppNumbers = async () => {
    try {
      const response = await whatsappAPI.getNumbers();
      setWhatsappNumbers(response.data.data || []);
    } catch (error) {
      console.error('Error fetching WhatsApp numbers:', error);
    }
  };

  const fetchConversations = async (pageToLoad = 1, append = false) => {
    try {
      if (append) {
        setLoadingMoreConversations(true);
      } else {
        setLoading(true);
      }

      const source = sourceFilter === 'all' ? null : sourceFilter;
      const response = await conversationsAPI.getAll(
        CONVERSATIONS_PAGE_SIZE,
        source,
        selectedWhatsAppNumber || null,
        selectedDepartment || null,
        pageToLoad
      );

      const newConversations = response.data.data || [];
      const hasMore = Boolean(response.data?.pagination?.hasMore);
      const total = Number(response.data?.pagination?.total || 0);

      if (append) {
        setConversations((prev) => [...prev, ...newConversations]);
      } else {
        setConversations(newConversations);
      }

      setConversationsPage(pageToLoad);
      setConversationsHasMore(hasMore);
      setConversationsTotal(total);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      if (append) {
        setLoadingMoreConversations(false);
      } else {
        setLoading(false);
      }
    }
  };

  const fetchUserChats = async (userId, userName, source, pageToLoad = 1, append = false) => {
    try {
      if (append) {
        setLoadingMoreUserChats(true);
      } else {
        setLoadingUserChats(true);
        setUserChats([]);
        setUserChatsHasMore(false);
      }

      const response = await conversationsAPI.getByUser(userId, USER_CHATS_PAGE_SIZE, source, pageToLoad);
      const newChats = response.data.data || [];
      const hasMore = Boolean(response.data?.pagination?.hasMore);
      const total = Number(response.data?.pagination?.total || 0);

      if (append) {
        setUserChats((prev) => [...prev, ...newChats]);
      } else {
        setUserChats(newChats);
      }

      setSelectedUser({ id: userId, name: userName, source });
      setUserChatsPage(pageToLoad);
      setUserChatsHasMore(hasMore);
      setUserChatsTotal(total);
    } catch (error) {
      console.error('Error fetching user chats:', error);
    } finally {
      if (append) {
        setLoadingMoreUserChats(false);
      } else {
        setLoadingUserChats(false);
      }
    }
  };

  const handleUserClick = async (userId, userName, source) => {
    fetchUserChats(userId, userName, source, 1, false);
  };

  const handleLoadMoreConversations = () => {
    fetchConversations(conversationsPage + 1, true);
  };

  const handleLoadMoreUserChats = () => {
    if (!selectedUser) return;
    fetchUserChats(selectedUser.id, selectedUser.name, selectedUser.source, userChatsPage + 1, true);
  };

  // Group conversations by customer
  const groupedUsers = {};
  filteredConversations.forEach(conv => {
    const key = `${conv.source}_${conv.customer_id}`;
    if (!groupedUsers[key]) {
      groupedUsers[key] = {
        userId: conv.customer_id,
        userName: conv.customer_name || 'Unknown',
        source: conv.source,
        department: conv.department,
        whatsappNumber: conv.whatsapp_number,
        lastMessage: conv.message,
        lastTime: conv.created_at,
        count: 1
      };
    } else {
      groupedUsers[key].count++;
    }
  });

  const uniqueUsers = Object.values(groupedUsers);

  // Get unique departments for filter
  const departments = [...new Set(conversations.filter(c => c.department).map(c => c.department))];
  const sortedUserChats = [...userChats].sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at)
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Conversations
        </Typography>
        <Typography variant="body1" color="text.secondary">
          View and search all chat conversations from Web Chat and WhatsApp
        </Typography>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <ToggleButtonGroup
            value={sourceFilter}
            exclusive
            onChange={(e, newValue) => {
              if (newValue !== null) {
                setSourceFilter(newValue);
                setSelectedUser(null);
              }
            }}
            size="small"
          >
            <ToggleButton value="all">
              <ChatIcon sx={{ mr: 1 }} fontSize="small" />
              All
            </ToggleButton>
            <ToggleButton value="web">
              <Computer sx={{ mr: 1 }} fontSize="small" />
              Web Chat
            </ToggleButton>
            <ToggleButton value="whatsapp">
              <WhatsApp sx={{ mr: 1 }} fontSize="small" />
              WhatsApp
            </ToggleButton>
          </ToggleButtonGroup>

          {sourceFilter !== 'web' && whatsappNumbers.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>WhatsApp Number</InputLabel>
              <Select
                value={selectedWhatsAppNumber}
                label="WhatsApp Number"
                onChange={(e) => {
                  setSelectedWhatsAppNumber(e.target.value);
                  setSelectedUser(null);
                }}
              >
                <MenuItem value="">
                  <em>All Numbers</em>
                </MenuItem>
                {whatsappNumbers.map((num) => (
                  <MenuItem key={num.id} value={num.id}>
                    {num.display_name} {num.department && `(${num.department})`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {sourceFilter !== 'web' && departments.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Department</InputLabel>
              <Select
                value={selectedDepartment}
                label="Department"
                onChange={(e) => {
                  setSelectedDepartment(e.target.value);
                  setSelectedUser(null);
                }}
              >
                <MenuItem value="">
                  <em>All Departments</em>
                </MenuItem>
                {departments.map((dept) => (
                  <MenuItem key={dept} value={dept}>
                    {dept}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
            {uniqueUsers.length} customer{uniqueUsers.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
      </Paper>

      <Box sx={{ display: 'flex', gap: 2, height: '65vh' }}>
        <Paper sx={{ width: '380px', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ p: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search conversations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Divider />

          <List sx={{ flex: 1, overflow: 'auto', p: 0 }}>
            {uniqueUsers.length > 0 ? (
              uniqueUsers.map((user, index) => (
                <ListItemButton
                  key={`${user.source}_${user.userId}_${index}`}
                  selected={selectedUser?.id === user.userId && selectedUser?.source === user.source}
                  onClick={() => handleUserClick(user.userId, user.userName, user.source)}
                  sx={{
                    borderLeft: 4,
                    borderColor: (selectedUser?.id === user.userId && selectedUser?.source === user.source) 
                      ? 'primary.main' 
                      : 'transparent',
                  }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: user.source === 'whatsapp' ? 'success.main' : 'primary.main' }}>
                      {user.source === 'whatsapp' ? <WhatsApp /> : user.userName?.charAt(0) || 'U'}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap>
                          {user.userName || 'Unknown'}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {user.source === 'whatsapp' && (
                            <Chip 
                              icon={<WhatsApp sx={{ fontSize: 14 }} />} 
                              label="WA" 
                              size="small" 
                              color="success"
                              sx={{ height: 20, fontSize: 10 }}
                            />
                          )}
                          <Chip label={user.count} size="small" color="primary" sx={{ height: 20 }} />
                        </Box>
                      </Box>
                    }
                    secondary={
                      <>
                        {user.whatsappNumber && (
                          <Typography variant="caption" color="success.main" sx={{ display: 'block', fontWeight: 500 }}>
                            {user.whatsappNumber}
                          </Typography>
                        )}
                        {user.department && (
                          <Chip 
                            label={user.department} 
                            size="small" 
                            variant="outlined"
                            sx={{ height: 16, fontSize: 10, mb: 0.5 }}
                          />
                        )}
                        <Typography variant="body2" noWrap sx={{ mb: 0.5 }}>
                          {user.lastMessage}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(user.lastTime).toLocaleString()}
                        </Typography>
                      </>
                    }
                  />
                </ListItemButton>
              ))
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2 }}>
                <ChatIcon sx={{ fontSize: 48, color: 'grey.300' }} />
                <Typography color="text.secondary">No conversations found</Typography>
              </Box>
            )}
          </List>

          {conversationsHasMore && (
            <Box sx={{ p: 1.5, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Loaded {conversations.length} of {conversationsTotal} messages
              </Typography>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleLoadMoreConversations}
                disabled={loadingMoreConversations}
              >
                {loadingMoreConversations ? 'Loading...' : 'Load More Customers'}
              </Button>
            </Box>
          )}

          {!conversationsHasMore && conversationsTotal > 0 && (
            <Box sx={{ p: 1.5, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary">
                Loaded {conversations.length} of {conversationsTotal} messages
              </Typography>
            </Box>
          )}
        </Paper>

        <Paper sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {selectedUser ? (
            <>
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar 
                  sx={{ 
                    bgcolor: selectedUser.source === 'whatsapp' ? 'success.main' : 'primary.main', 
                    width: 56, 
                    height: 56 
                  }}
                >
                  {selectedUser.source === 'whatsapp' ? <WhatsApp /> : selectedUser.name?.charAt(0) || 'U'}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {selectedUser.name || 'Unknown User'}
                    </Typography>
                    <Chip 
                      label={selectedUser.source === 'whatsapp' ? 'WhatsApp' : 'Web Chat'}
                      size="small"
                      color={selectedUser.source === 'whatsapp' ? 'success' : 'primary'}
                      icon={selectedUser.source === 'whatsapp' ? <WhatsApp /> : <Computer />}
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {selectedUser.id}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ flex: 1, overflow: 'auto', p: 2, bgcolor: 'grey.50' }}>
                {loadingUserChats && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={24} />
                  </Box>
                )}

                {sortedUserChats.map((chat, index) => (
                  <React.Fragment key={chat.id || index}>
                    {(index === 0 ||
                      new Date(chat.created_at).toDateString() !==
                        new Date(sortedUserChats[index - 1].created_at).toDateString()) && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                        <Chip
                          label={getDateGroupLabel(chat.created_at)}
                          size="small"
                          sx={{ bgcolor: 'grey.200', fontWeight: 600 }}
                        />
                      </Box>
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                      <Box sx={{ maxWidth: '70%' }}>
                        <Paper
                          sx={{
                            p: 2,
                            bgcolor: chat.source === 'whatsapp' ? 'success.main' : 'primary.main',
                            color: 'white',
                            borderRadius: 2,
                            borderBottomRightRadius: 4,
                          }}
                        >
                          <Typography variant="body1">{chat.message}</Typography>
                        </Paper>
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, textAlign: 'right' }}>
                          {new Date(chat.created_at).toLocaleTimeString()}
                        </Typography>
                      </Box>
                    </Box>

                    {chat.response && (
                      <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
                        <Box sx={{ maxWidth: '70%' }}>
                          <Paper
                            sx={{
                              p: 2,
                              bgcolor: 'white',
                              borderRadius: 2,
                              borderBottomLeftRadius: 4,
                            }}
                          >
                            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                              {chat.response}
                            </Typography>
                          </Paper>
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                            Bot • {new Date(chat.created_at).toLocaleTimeString()}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </React.Fragment>
                ))}

                {!loadingUserChats && userChatsHasMore && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        Loaded {userChats.length} of {userChatsTotal} messages
                      </Typography>
                      <Button
                        variant="outlined"
                        onClick={handleLoadMoreUserChats}
                        disabled={loadingMoreUserChats}
                      >
                        {loadingMoreUserChats ? 'Loading...' : 'Load Older Messages'}
                      </Button>
                    </Box>
                  </Box>
                )}

                {!loadingUserChats && !userChatsHasMore && userChatsTotal > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Loaded {userChats.length} of {userChatsTotal} messages
                    </Typography>
                  </Box>
                )}
              </Box>
            </>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2 }}>
              <Person sx={{ fontSize: 64, color: 'grey.300' }} />
              <Typography color="text.secondary">Select a conversation to view chat history</Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default Conversations;
